/**
 * OrbAgent.js
 * ────────────
 * Conversational text-first interface to the Orb agent.
 *   • POST /api/orb/agent/chat — multi-turn loop with Claude Sonnet 4.5
 *   • Pulsing visual feedback while the agent is "thinking"
 *   • Entity pills appear when the model extracts data (city, date, ...)
 *
 * This is a standalone page reachable at /orb-chat — the existing voice
 * Orb on /orb keeps its STT/TTS pipeline; this page is the typed-input
 * entrypoint the user can also reach by tapping "Schreiben" on the orb.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Send, Loader2, MapPin, Clock, Wallet, Sparkles, RefreshCw,
  Building2, CreditCard, Users, CheckCircle2, ExternalLink, Smartphone,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '../utils/apiConfig';

const SESSION_PREFIX = 'orb-chat-';

export default function OrbAgent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);   // {role, text, entities?, ridesPreview?}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => SESSION_PREFIX + Date.now());
  const scrollRef = useRef(null);
  const autoRanRef = useRef(false);

  // Auto-scroll on new message
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = useCallback(async (text) => {
    if (!text.trim() || busy) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/orb/agent/chat`, {
        text: text.trim(), session_id: sessionId, lang: i18n.language || 'de',
      });
      const data = r.data || {};
      const ridesPreview = data.results?.search_rides?.rides;
      const wizardResult = data.results?.create_b2b_subscription_voice || null;
      setMessages(m => [...m, {
        role: 'assistant',
        text: data.reply || '…',
        entities: data.entities || {},
        toolsUsed: data.tools_used || [],
        ridesPreview: Array.isArray(ridesPreview) ? ridesPreview : null,
        wizardResult,
      }]);
    } catch (e) {
      setMessages(m => [...m, {
        role: 'assistant',
        text: t('orbAgent.error', 'Hmm, da ist gerade etwas schiefgegangen — versuch es nochmal.'),
        error: true,
      }]);
    } finally {
      setBusy(false);
    }
  }, [busy, sessionId, i18n.language, t]);

  const reset = async () => {
    try { await axios.post(`${API}/orb/agent/reset`, { session_id: sessionId }); } catch {}
    setSessionId(SESSION_PREFIX + Date.now());
    setMessages([]);
  };

  // ── URL-Param auto-prompt (deep-link from /preise-b2b) ─────────
  // ?wizard=b2b&plan=<key>&auto=1 → pre-fills (and optionally auto-sends)
  // the seeded utterance that triggers create_b2b_subscription_voice.
  useEffect(() => {
    if (autoRanRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('wizard') !== 'b2b') return;
    autoRanRef.current = true;
    const plan = (params.get('plan') || 'pro').toLowerCase();
    const planName = plan.charAt(0).toUpperCase() + plan.slice(1);
    const seedText = t('orbWizard.seedPrompt',
      'Hey Orb, ich will mein Unternehmen auf den {{plan}}-Plan anmelden.',
      { plan: planName }
    );
    if (params.get('auto') === '1') {
      // small delay so the page paints before send() blocks
      const id = setTimeout(() => send(seedText), 450);
      return () => clearTimeout(id);
    } else {
      setInput(seedText);
    }
  }, [send, t]);

  return (
    <div data-testid="orb-agent-page" style={s.page}>
      <header style={s.header}>
        <button data-testid="orb-agent-back" onClick={() => navigate(-1)} style={s.backBtn}>
          <ArrowLeft size={16} /> {t('common.back', 'Zurück')}
        </button>
        <h1 style={s.title}>
          <Sparkles size={20} color="#a855f7" />
          {t('orbAgent.title', 'Orb-Assistent')}
        </h1>
        <button data-testid="orb-agent-reset" onClick={reset} style={s.resetBtn} title={t('orbAgent.reset', 'Neu starten')}>
          <RefreshCw size={14} />
        </button>
      </header>

      <div ref={scrollRef} style={s.messages}>
        {messages.length === 0 && (
          <div data-testid="orb-agent-hint" style={s.hint}>
            <p style={{ marginBottom: 14 }}>{t('orbAgent.hint',
              'Frag mich alles — ob Wallet, Fahrt nach Berlin, Stornierung oder Hilfe. Ich kümmere mich.')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                'Wie viel ist auf meinem Wallet?',
                'Mitfahrt von Hamburg nach Berlin morgen abend',
                'Was passiert, wenn der Fahrer zu spät kommt?',
                'Ich hab mein Passwort vergessen',
              ].map((x, i) => (
                <button key={x} onClick={() => send(x)} style={s.exampleBtn} data-testid={`orb-example-${i}`}>
                  „{x}"
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <Bubble key={i} m={m} />
        ))}

        {busy && <PulsingBubble />}
      </div>

      <form onSubmit={e => { e.preventDefault(); send(input); }} style={s.form}>
        <input
          data-testid="orb-agent-input"
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={t('orbAgent.placeholder', 'Schreib eine Nachricht…')}
          disabled={busy}
          style={s.input}
        />
        <button
          data-testid="orb-agent-send"
          type="submit"
          disabled={busy || !input.trim()}
          style={{ ...s.sendBtn, opacity: busy || !input.trim() ? 0.5 : 1 }}
        >
          {busy ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}


function Bubble({ m }) {
  const isUser = m.role === 'user';
  const ent = m.entities || {};
  const hasPills = !isUser && (ent.from_city || ent.to_city || ent.date || ent.time_of_day);

  // Show wallet result inline
  const wallet = m.toolsUsed?.includes('get_wallet_balance');

  return (
    <div data-testid={`orb-msg-${m.role}`} style={{ ...s.bubbleRow, justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <div style={{ ...s.bubble, ...(isUser ? s.bubbleUser : s.bubbleBot), ...(m.error ? s.bubbleError : {}) }}>
        <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>

        {hasPills && (
          <div data-testid="orb-pills" style={s.pills}>
            {ent.from_city && <Pill icon={<MapPin size={10} />} label={`${ent.from_city}`} />}
            {ent.to_city && <Pill icon="→" label={`${ent.to_city}`} />}
            {ent.date && <Pill icon={<Clock size={10} />} label={ent.date} />}
            {ent.time_of_day && <Pill label={ent.time_of_day} />}
          </div>
        )}

        {wallet && !ent.from_city && (
          <div style={s.walletPill}>
            <Wallet size={12} /> {m.toolsUsed.length} action(s) executed
          </div>
        )}

        {Array.isArray(m.ridesPreview) && m.ridesPreview.length > 0 && (
          <div data-testid="orb-rides-preview" style={s.ridesPreview}>
            {m.ridesPreview.slice(0, 3).map(r => (
              <div key={r.id} style={s.rideCard}>
                <div style={{ fontWeight: 700 }}>{r.departure || '–'}</div>
                <div style={{ color: '#94a3b8', fontSize: 12 }}>{r.seats} Sitz(e) frei</div>
                {r.price != null && <div style={{ color: '#22c55e', fontWeight: 700 }}>{Number(r.price).toFixed(2)} €</div>}
              </div>
            ))}
          </div>
        )}

        {m.wizardResult && <WizardOverlay result={m.wizardResult} />}
      </div>
    </div>
  );
}


// ── B2B Voice-Wizard visual support ────────────────────────────────
// Pills zeigen den Multi-Turn-Status, Checkout-Card erscheint bei status='ok'.
function WizardOverlay({ result }) {
  const { t } = useTranslation();
  const status = result.status;
  const field = result.field;

  // Step progression: plan → seats → company → confirm → done
  const stepOrder = ['plan_key', 'seats', 'company_name', 'confirm', 'done'];
  let currentStep = 0;
  if (status === 'missing_field') {
    currentStep = stepOrder.indexOf(field);
    if (currentStep < 0) currentStep = 0;
  } else if (status === 'needs_confirmation') {
    currentStep = stepOrder.indexOf('confirm');
  } else if (status === 'ok') {
    currentStep = stepOrder.indexOf('done');
  }

  const steps = [
    { key: 'plan_key',     label: t('orbWizard.stepPlan',   'Plan'),       icon: <CreditCard size={11} /> },
    { key: 'seats',        label: t('orbWizard.stepSeats',  'Sitze'),      icon: <Users size={11} /> },
    { key: 'company_name', label: t('orbWizard.stepCompany','Firma'),      icon: <Building2 size={11} /> },
    { key: 'confirm',      label: t('orbWizard.stepConfirm','Bestätigen'), icon: <CheckCircle2 size={11} /> },
    { key: 'done',         label: t('orbWizard.stepDone',   'Fertig'),     icon: <Sparkles size={11} /> },
  ];

  return (
    <div data-testid="orb-wizard-overlay" style={s.wizardWrap}>
      <div style={s.wizardHead}>
        <Building2 size={13} color="#a855f7" />
        <span style={s.wizardTitle}>
          {t('orbWizard.title', 'B2B-Onboarding-Wizard')}
        </span>
      </div>
      <div style={s.wizardPills} data-testid="orb-wizard-pills">
        {steps.map((step, i) => {
          const done = i < currentStep;
          const active = i === currentStep && status !== 'ok';
          const finished = status === 'ok' && i === steps.length - 1;
          return (
            <React.Fragment key={step.key}>
              <div
                data-testid={`orb-wizard-pill-${step.key}`}
                style={{
                  ...s.wizardPill,
                  ...(done || finished ? s.wizardPillDone : {}),
                  ...(active ? s.wizardPillActive : {}),
                }}
              >
                {step.icon}
                <span>{step.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div style={{
                  ...s.wizardConnector,
                  background: i < currentStep
                    ? 'linear-gradient(90deg, #a855f7, #6366f1)'
                    : 'rgba(148,163,184,0.2)',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {status === 'ok' && result.checkout_url && (
        <CheckoutCard
          url={result.checkout_url}
          amount={result.amount_eur}
          plan={result.plan_key}
          seats={result.seats}
        />
      )}

      {status === 'needs_confirmation' && (
        <div style={s.confirmHint} data-testid="orb-wizard-confirm-hint">
          <strong>{Number(result.amount_eur).toFixed(2)} €</strong>{' '}
          {t('orbWizard.perMonth', '/ Monat')} ·{' '}
          {result.seats} {t('orbWizard.seats', 'Sitze')} ·{' '}
          {result.plan_key?.toUpperCase()}
        </div>
      )}

      {status === 'error' && result.code && (
        <div style={s.wizardError} data-testid="orb-wizard-error">
          {result.code}
        </div>
      )}
    </div>
  );
}


function CheckoutCard({ url, amount, plan, seats }) {
  const { t } = useTranslation();
  return (
    <div data-testid="orb-checkout-card" style={s.checkoutCard}>
      <div style={s.checkoutHead}>
        <CheckCircle2 size={16} color="#22c55e" />
        <span style={s.checkoutTitle}>
          {t('orbWizard.checkoutReady', 'Stripe-Checkout bereit')}
        </span>
      </div>
      <div style={s.checkoutBody}>
        <div style={s.checkoutQr}>
          <QRCodeSVG
            value={url}
            size={120}
            bgColor="#ffffff"
            fgColor="#0f172a"
            level="M"
            includeMargin={true}
          />
          <div style={s.checkoutQrLabel}>
            <Smartphone size={11} /> {t('orbWizard.scanMobile', 'Mobil scannen')}
          </div>
        </div>
        <div style={s.checkoutInfo}>
          <div style={s.checkoutAmount}>
            {Number(amount).toFixed(2)} €
            <span style={s.checkoutPeriod}>/ {t('orbWizard.month', 'Monat')}</span>
          </div>
          <div style={s.checkoutMeta}>
            <span style={s.checkoutMetaItem}>
              <CreditCard size={12} /> {(plan || '').toUpperCase()}
            </span>
            <span style={s.checkoutMetaItem}>
              <Users size={12} /> {seats} {t('orbWizard.seats', 'Sitze')}
            </span>
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={s.checkoutBtn}
            data-testid="orb-checkout-btn"
          >
            <ExternalLink size={14} />
            {t('orbWizard.openCheckout', 'Zur Stripe-Kasse')}
          </a>
        </div>
      </div>
    </div>
  );
}


function Pill({ icon, label }) {
  return (
    <span style={s.pill}>
      {typeof icon === 'string' ? <span>{icon}</span> : icon}
      {label}
    </span>
  );
}


function PulsingBubble() {
  return (
    <div data-testid="orb-thinking" style={{ ...s.bubbleRow, justifyContent: 'flex-start' }}>
      <div style={{ ...s.bubble, ...s.bubbleBot }}>
        <div style={s.dots}>
          <span style={{ ...s.dot, animationDelay: '0s' }} />
          <span style={{ ...s.dot, animationDelay: '0.18s' }} />
          <span style={{ ...s.dot, animationDelay: '0.36s' }} />
        </div>
      </div>
      <style>{`
        @keyframes orbpulse {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes wizardPulse {
          0%, 100% { box-shadow: 0 0 0 1px #a855f7, 0 0 12px rgba(168,85,247,0.4); }
          50%      { box-shadow: 0 0 0 1px #a855f7, 0 0 20px rgba(168,85,247,0.7); }
        }
      `}</style>
    </div>
  );
}


// ── Styles ────────────────────────────────────────────────────────
const s = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column',
          background: 'radial-gradient(circle at 30% 0%, #1e1b4b 0%, #0b1220 60%, #020617 100%)',
          color: '#f1f5f9' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.06)' },
  backBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
             borderRadius: 8, padding: '6px 10px', color: '#cbd5e1', cursor: 'pointer',
             display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  title: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: 0, flex: 1 },
  resetBtn: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8, padding: 6, color: '#cbd5e1', cursor: 'pointer',
              display: 'flex' },
  messages: { flex: 1, overflowY: 'auto', padding: 18, display: 'flex',
              flexDirection: 'column', gap: 12, maxWidth: 720, width: '100%',
              margin: '0 auto' },
  hint: { textAlign: 'left', color: '#94a3b8', fontSize: 14, padding: 16,
          background: 'rgba(255,255,255,0.03)', borderRadius: 14,
          border: '1px solid rgba(255,255,255,0.06)' },
  exampleBtn: { background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.18)',
                color: '#e9d5ff', padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
                fontSize: 13, textAlign: 'left' },

  bubbleRow: { display: 'flex' },
  bubble: { maxWidth: '78%', padding: '10px 14px', borderRadius: 16,
            fontSize: 15, lineHeight: 1.45, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
  bubbleUser: { background: 'linear-gradient(135deg,#6366f1,#a855f7)', color: '#fff',
                borderBottomRightRadius: 4 },
  bubbleBot: { background: '#1e293b', color: '#e2e8f0',
               border: '1px solid rgba(255,255,255,0.06)',
               borderBottomLeftRadius: 4 },
  bubbleError: { background: '#7f1d1d', color: '#fee2e2' },

  pills: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  pill: { display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 9px', borderRadius: 999, fontSize: 11,
          background: 'rgba(34,197,94,0.18)', color: '#bbf7d0',
          border: '1px solid rgba(34,197,94,0.35)' },
  walletPill: { display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 9px', borderRadius: 999, fontSize: 11,
                background: 'rgba(59,130,246,0.18)', color: '#bfdbfe',
                marginTop: 8 },

  ridesPreview: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))',
                  gap: 8, marginTop: 10 },
  rideCard: { padding: 8, background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
              fontSize: 12 },

  dots: { display: 'flex', gap: 5, padding: '4px 2px' },
  dot: { width: 7, height: 7, borderRadius: '50%', background: '#a855f7',
         display: 'inline-block', animation: 'orbpulse 1.4s infinite ease-in-out both' },

  form: { display: 'flex', gap: 8, padding: 14,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          maxWidth: 720, width: '100%', margin: '0 auto', boxSizing: 'border-box' },
  input: { flex: 1, padding: '12px 14px', borderRadius: 12,
           background: 'rgba(2,6,23,0.6)', color: '#f1f5f9',
           border: '1px solid rgba(255,255,255,0.1)', outline: 'none', fontSize: 15 },
  sendBtn: { padding: '0 18px', borderRadius: 12, border: 'none',
             background: 'linear-gradient(135deg,#6366f1,#a855f7)',
             color: '#fff', cursor: 'pointer', display: 'flex',
             alignItems: 'center', gap: 6, fontWeight: 700 },

  // ── B2B Voice-Wizard styles ─────────────────────────────────────
  wizardWrap: {
    marginTop: 12, padding: 12, background: 'rgba(168,85,247,0.06)',
    border: '1px solid rgba(168,85,247,0.22)', borderRadius: 12,
  },
  wizardHead: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 },
  wizardTitle: { fontSize: 12, fontWeight: 700, color: '#e9d5ff',
                 textTransform: 'uppercase', letterSpacing: 0.6 },
  wizardPills: { display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' },
  wizardPill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 9px', borderRadius: 999, fontSize: 11,
    background: 'rgba(255,255,255,0.04)', color: '#94a3b8',
    border: '1px solid rgba(148,163,184,0.18)', transition: 'all .3s',
  },
  wizardPillActive: {
    background: 'rgba(168,85,247,0.22)', color: '#f3e8ff',
    border: '1px solid #a855f7',
    boxShadow: '0 0 0 1px #a855f7, 0 0 12px rgba(168,85,247,0.4)',
    animation: 'wizardPulse 1.6s infinite ease-in-out',
  },
  wizardPillDone: {
    background: 'rgba(34,197,94,0.18)', color: '#bbf7d0',
    border: '1px solid rgba(34,197,94,0.4)',
  },
  wizardConnector: { width: 14, height: 2, borderRadius: 1 },
  confirmHint: {
    marginTop: 10, padding: '8px 12px', background: 'rgba(245,158,11,0.1)',
    border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10,
    fontSize: 13, color: '#fbbf24', textAlign: 'center',
  },
  wizardError: {
    marginTop: 10, padding: '8px 12px', background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10,
    fontSize: 12, color: '#fca5a5', textAlign: 'center', fontFamily: 'monospace',
  },

  // ── Checkout Card with QR ───────────────────────────────────────
  checkoutCard: {
    marginTop: 12, padding: 14,
    background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(22,163,74,0.06))',
    border: '1px solid rgba(34,197,94,0.35)', borderRadius: 14,
  },
  checkoutHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 },
  checkoutTitle: { fontSize: 13, fontWeight: 700, color: '#bbf7d0' },
  checkoutBody: { display: 'flex', gap: 14, alignItems: 'center' },
  checkoutQr: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  checkoutQrLabel: {
    fontSize: 10, color: '#94a3b8', display: 'inline-flex', alignItems: 'center', gap: 3,
  },
  checkoutInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 8 },
  checkoutAmount: {
    fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1,
    display: 'flex', alignItems: 'baseline', gap: 4,
  },
  checkoutPeriod: { fontSize: 12, opacity: 0.6, fontWeight: 400 },
  checkoutMeta: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  checkoutMetaItem: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', background: 'rgba(255,255,255,0.06)',
    borderRadius: 8, fontSize: 11, color: '#cbd5e1',
  },
  checkoutBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '10px 14px', borderRadius: 10, textDecoration: 'none',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: '#fff', fontWeight: 700, fontSize: 13,
    boxShadow: '0 4px 16px rgba(34,197,94,0.4)',
  },
};
